import { useEffect, useRef, useState } from 'react';

// Live preview of the Ad Library URL a keyword's settings would search.
//
// The URL is built by the BACKEND, with the same function the scraper navigates
// to. Building it here would be faster and wrong: that module pins a fussy
// parameter order and literal brackets that its tests assert character for
// character, and a second implementation would drift into showing a link that
// is not the search being run — which is the one thing this preview exists to
// disprove.

export interface KeywordSearchUrlInput {
  keyword: string;
  dateRangePreset?: string;
  startDateMin?: string | null;
  startDateMax?: string | null;
  pageIds?: string[];
  pageGroupIds?: string[];
}

export interface KeywordSearchUrlResult {
  url: string | null;
  advertiserCount: number;
  loading: boolean;
}

/**
 * @param fetcher  the tool's own service call, so video previews a video search
 *                 and static previews a static one
 * @param input    the CURRENT form values, not the saved row — the point is to
 *                 see what you are about to save
 * @param enabled  false while the dialog is closed, so editing nothing costs nothing
 */
export default function useKeywordSearchUrl(
  fetcher: (input: KeywordSearchUrlInput) => Promise<{ url: string; advertiserCount: number } | null>,
  input: KeywordSearchUrlInput,
  enabled: boolean,
): KeywordSearchUrlResult {
  const [url, setUrl] = useState<string | null>(null);
  const [advertiserCount, setAdvertiserCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Serialized so the effect re-runs on VALUE changes rather than on every
  // render — the arrays are rebuilt by the parent each time.
  const key = JSON.stringify(input);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) { setUrl(null); return undefined; }

    const parsed: KeywordSearchUrlInput = JSON.parse(key);
    if (!parsed.keyword?.trim()) { setUrl(null); return undefined; }

    // Debounced: this fires on every keystroke in the keyword field, and the
    // request resolves page groups to their members in Snowflake.
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      void fetcherRef.current(parsed)
        .then((resp) => {
          if (cancelled) return;
          setUrl(resp?.url || null);
          setAdvertiserCount(resp?.advertiserCount ?? 0);
        })
        // A preview that cannot be built is not worth an error banner over the
        // form — the link simply doesn't appear.
        .catch(() => { if (!cancelled) setUrl(null); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 400);

    return () => { cancelled = true; clearTimeout(timer); setLoading(false); };
  }, [key, enabled]);

  return { url, advertiserCount, loading };
}
